import { Calendar, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import LinkedInLogo from '@/assets/icons/linkedin-logo.svg?react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { buttonVariants } from '@/components/ui/button';
import Pagination from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ContactDataType } from '@/constants/contacts/dummy';
import { normalizeLinkedInUrl } from '@/lib/string';
import { cn } from '@/lib/ui/utils';

interface LeadershipTeamTableProps {
  data: ContactDataType[];
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const LeadershipTeamTable = ({
  data,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
}: LeadershipTeamTableProps) => {
  const renderContactCard = (item: ContactDataType) => (
    <div
      key={item.id}
      className="flex flex-col gap-4 rounded-xl border border-border bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <Avatar className="size-10">
          <AvatarImage src={item.photo_url} alt={item.full_name} />
          <AvatarFallback>
            <UserIcon className="size-5" />
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="as-title-03 as-strong">{item.full_name}</p>
          <p className="as-body-03 text-muted-foreground">{item.position}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to={normalizeLinkedInUrl(item.linkedin)}
          rel="noopener noreferrer"
          target="_blank"
        >
          <LinkedInLogo className="size-6" />
        </Link>
        <Link
          to={item.meeting_link || ''}
          rel="noopener noreferrer"
          target="_blank"
          className={cn(
            buttonVariants({ size: 'sm', variant: 'outline' }),
            'flex-1'
          )}
        >
          <Calendar className="size-4" />
          Book
        </Link>
      </div>
    </div>
  );

  const renderTableRow = (item: ContactDataType) => (
    <TableRow key={item.id}>
      <TableCell className="px-4 text-typo-gray-secondary">
        <div className="flex items-center justify-start gap-2">
          <Avatar className="size-10">
            <AvatarImage src={item.photo_url} alt={item.full_name} />
            <AvatarFallback>
              <UserIcon className="size-5" />
            </AvatarFallback>
          </Avatar>
          <p>{item.full_name}</p>
        </div>
      </TableCell>
      <TableCell className="text-typo-gray-secondary">
        {item.position}
      </TableCell>
      <TableCell className="text-typo-gray-secondary">{item.email}</TableCell>
      <TableCell className="text-typo-gray-secondary">
        <Link
          to={normalizeLinkedInUrl(item.linkedin)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <LinkedInLogo className="size-6" />
        </Link>
      </TableCell>
      <TableCell>
        <Link
          to={item.meeting_link || ''}
          rel="noopener noreferrer"
          target="_blank"
          className={buttonVariants({ size: 'sm', variant: 'outline' })}
        >
          <Calendar className="size-4" />
          Book
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <div className="space-y-4 md:hidden">
        {data.length === 0 ? (
          <div className="rounded-xl border border-border bg-white p-4 text-center text-muted-foreground">
            No leadership team members found
          </div>
        ) : (
          data.map(renderContactCard)
        )}
        <div className="pt-2">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-alpha-dark-5 bg-muted max-md:hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="as-title-04 px-4">Name</TableHead>
              <TableHead className="as-title-04">Position in Company</TableHead>
              <TableHead className="as-title-04">Email</TableHead>
              <TableHead className="as-title-04">LinkedIn</TableHead>
              <TableHead className="as-title-04">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white">
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center text-muted-foreground"
                >
                  No leadership team members found
                </TableCell>
              </TableRow>
            ) : (
              data.map(renderTableRow)
            )}
          </TableBody>
        </Table>
        <div className="border-t border-alpha-dark-5 px-4 py-3">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>
    </>
  );
};

export { LeadershipTeamTable };
